import { App } from '@capacitor/app';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, Suspense, lazy } from 'react';
import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';

import type { LoadableComponent } from '@loadable/component';
import type { ComponentType } from 'react';
import type { LayoutProps } from './LayoutProps.type';

import { COMPANY_ID_LOCAL_STORAGE_KEY, getCompanyStatus, getCurrentCompanyId, isExpiredToken } from './Utils';
import Button from 'Components/UI_Library/FormElements/Button';
import { getAttachedCompanies } from 'Backend/users/utils/attachedCompanies';
import { isOktaInternalAuthToken } from './Utils/authToken';
import Config from 'Config';

const GeneralModal = lazy(() => import('Components/UI_Library/Modals/GeneralModal'));

export const platform = Capacitor.getPlatform();
const isMobileApp = platform === 'ios' || platform === 'android';

const PrivateRoute = (props: {
    component: ComponentType | LoadableComponent<object>;
    layout: ComponentType<LayoutProps>;
    lock?: ComponentType | LoadableComponent<object>;
    cardsOnly?: unknown;
    ignorePageTitle?: unknown;
    breadcrumbTitle?: string;
    title?: string | JSX.Element;
    focus?: boolean;
    fullWidth?: boolean;
    oktaInternalOnly?: boolean;
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { component: Component, layout: Layout, lock: Lock, cardsOnly, ...rest } = props;
    const [authState, setAuthState] = useState<'loading' | 'success' | 'expired' | 'unauthorised' | 'error'>('loading');
    const [showWarningModal, setShowWarningModal] = useState<boolean>(false);

    useEffect(() => {
        isExpiredToken()
            .then((isExpired) => {
                if (isExpired) {
                    setAuthState('expired');
                    return;
                }
                if (rest.oktaInternalOnly && !isOktaInternalAuthToken()) {
                    setAuthState('unauthorised');
                    return;
                }
                setAuthState('success');
            })
            .catch(() => setAuthState('error'));

        return () => {
            // NOTE: stop listening events on component unmount
            handleMultiTabUserWarning(true);
            handleMobileAppOnBlur(true);
        };
    }, []);

    useEffect(() => {
        if (authState === 'success') {
            handleMultiTabUserWarning();
            handleMobileAppOnBlur();
        } else {
            handleMobileAppOnBlur(true);
        }
    }, [authState]);

    if (!rest.ignorePageTitle) {
        if (rest.breadcrumbTitle && typeof rest.breadcrumbTitle === 'string') {
            document.title = `Paytron - ${rest.breadcrumbTitle}`;
        } else if (rest.title && typeof rest.title === 'string') {
            document.title = `Paytron - ${rest.title}`;
        } else {
            document.title = 'Paytron';
        }
    }
    let destinationPath = '/signin';
    const currentPath = window.location.pathname.split('/');
    if (currentPath.length > 1 && currentPath[1] === 'connect') {
        destinationPath = '/connect/signin';
    }

    // Get the current pathname e.g. /payments/all and add to redirect query param. Used for redirecting back to previous route
    const currentPathname = encodeURIComponent(window.location.pathname + window.location.search);
    const search = window.location.search
        ? `${window.location.search}&redirect=${currentPathname}`
        : `?redirect=${currentPathname}`;

    const companyStatus = getCompanyStatus();

    type LockedReturnType = NonNullable<typeof Lock>;
    /** //NOTE: The type predicate is technically incorrect because it can return false negatives. It's intentional as
     * there are more conditions for whether this is locked and Lock should be used even if Lock is non-nullish.  */
    const locked = (lock: typeof Lock): lock is LockedReturnType => {
        if (companyStatus === 'APPROVED') return false;
        if (lock && cardsOnly && ['APPROVED', 'APPROVED - CARDS ONLY'].includes(companyStatus)) return false; // NOTE: False negative for type predicate
        if (lock) {
            lock satisfies LockedReturnType;
            return true;
        }
        return false;
    };

    const handleMultiTabUserWarning = (shouldStopListener?: boolean): void => {
        const handler = (e: StorageEvent) => {
            // NOTE: Check if user switch accounts in another tab
            if (e.key === COMPANY_ID_LOCAL_STORAGE_KEY) {
                setShowWarningModal(true);
            }
        };

        if (shouldStopListener) {
            window.removeEventListener('storage', handler);
            return;
        }

        // NOTE: Listen for changes to the localStorage
        window.addEventListener('storage', handler);
    };

    const selectedCompany = getAttachedCompanies().find((c) => c.companyId === getCurrentCompanyId());

    const handleMobileAppOnBlur = async (shouldStopListener?: boolean) => {
        if (!isMobileApp) return;

        await App.removeAllListeners();

        if (shouldStopListener) return;

        const biometricsAvailable = await NativeBiometric.isAvailable();

        let isBiometricCheckPending = false;
        const htmlElement = document.getElementsByTagName('html')[0];
        const blurringStyle = 'filter: blur(10px); pointer-events: none;';

        const handleAppStateChange = async ({ isActive }: { isActive: boolean }) => {
            if (!isActive) isBiometricCheckPending = true;
            const elementDefaultStyle = htmlElement?.getAttribute('style');

            if (biometricsAvailable && isBiometricCheckPending) {
                if (!isActive) htmlElement?.setAttribute('style', elementDefaultStyle + blurringStyle);

                const timeout = setTimeout(async () => {
                    const isVerified = await NativeBiometric.verifyIdentity()
                        .then(() => true)
                        .catch(() => false);

                    isBiometricCheckPending = false;

                    if (!isVerified && isActive) navigate(`/signin?redirect=${location.pathname}`);

                    // remove blurring style only
                    const currentStyles = htmlElement?.getAttribute('style');
                    if (currentStyles && currentStyles.includes(blurringStyle)) {
                        const styleArray = currentStyles.split(';');
                        const filteredStyles = styleArray.filter(
                            (style) =>
                                !(style.trim() === 'filter: blur(10px)' || style.trim() === 'pointer-events: none')
                        );
                        const newStyles = filteredStyles.join(';').trim();
                        htmlElement?.setAttribute('style', newStyles);
                    }
                }, 500);

                if (!isActive) clearTimeout(timeout);
            }
        };

        await App.addListener('appStateChange', handleAppStateChange);
    };

    switch (authState) {
        case 'success':
            return (
                <Layout
                    title={rest.title}
                    breadcrumbTitle={rest.breadcrumbTitle}
                    focus={rest.focus}
                    fullWidth={rest.fullWidth}
                >
                    <>
                        {showWarningModal && (
                            <Suspense>
                                <GeneralModal
                                    isOpen
                                    close={() => {}} // NOTE: close is empty so click on overlay wont close the modal (ps.: nah, shouldCloseOnOverlayClick prop doesn't work properly)
                                    hideDismissBtn
                                    title="Active account switched"
                                    style={{
                                        backgroundColor: 'inherit',
                                        border: 'none',
                                        padding: '15px 0 10px',
                                    }}
                                    modalStyle={{
                                        padding: '24px 32px',
                                    }}
                                    maxWidth={436}
                                    controls={
                                        <Button
                                            style={{
                                                width: '100%',
                                            }}
                                            type="primary"
                                            onClick={() => {
                                                window.location.reload();
                                            }}
                                            text="Refresh"
                                        />
                                    }
                                >
                                    The current account being viewed has been switched to{' '}
                                    {selectedCompany?.companyName ?? 'a different company'} in another tab.
                                </GeneralModal>
                            </Suspense>
                        )}
                        {locked(Lock) ? <Lock /> : <Component />}
                    </>
                </Layout>
            );
        case 'expired':
        case 'error':
            return (
                <Navigate
                    to={{
                        pathname: destinationPath,
                        search,
                    }}
                />
            );
        case 'unauthorised':
            window.location.href = Config.paytronURL;
            return null;
        default:
            return null;
    }
};

export default PrivateRoute;
